import { ManualGroup } from './user-manual.model';

export const MANUAL_GROUPS: ManualGroup[] = [
  {
    id: 'getting-started',
    section: 'users',
    title: 'Getting started',
    topics: [
      {
        id: 'users-page',
        title: 'The Users page',
        tagline: 'Where every user setting lives',
        icon: 'book',
        intro:
          'Users is the single place where accounts are created and where coverage, backup and grouping are set. Everything in this manual starts from this page.',
        steps: [
          {
            title: 'Open the page',
            body:
              'Select Users in the left sidebar. The page opens with a count of total users, how many have a backup assigned, and how many backups are active right now.',
          },
          {
            title: 'Find a user',
            body:
              'Use the Search box to look someone up by name or email, or narrow the list with the All Groups filter to see only one user group at a time.',
          },
          {
            title: 'Open the user panel',
            body:
              'Select Add New User to create an account, or the edit icon on a row to change one. Both open the same side panel, which is where coverage, backup operator and group are set.',
            callout: {
              kind: 'note',
              text:
                'The coverage, manual assignment and backup sections only appear once a role is chosen. They are tied to the manager and operator roles.',
            },
          },
          {
            title: 'Save',
            body:
              'Nothing in the panel is stored until you save. Select Update at the bottom of the panel to write the account, its shift plan and its backup together.',
            callout: {
              kind: 'warning',
              text:
                'If a shift panel is still open when you save, a warning appears at the top of that panel. Save or cancel the shift first, then save the user.',
            },
          },
        ],
        faqs: [
          {
            q: 'Why can I not see the Coverage Method section?',
            a: 'It only shows for the manager and operator roles. Pick one of those in the Role field and the section appears.',
          },
          {
            q: 'Which clock do the times use?',
            a: 'Every date and time in the user panel is New York time, because alert routing and shift hours are read in that clock. Your own timezone does not apply.',
          },
        ],
      },
    ],
  },
  {
    id: 'coverage',
    section: 'users',
    title: 'Coverage & scheduling',
    topics: [
      {
        id: 'schedule-coverage',
        title: 'Schedule coverage',
        tagline: 'Build a repeating weekly plan',
        icon: 'calendar',
        intro:
          'Scheduled Coverage gives a user a weekly shift plan. Live alerts follow whichever scheduled shift is active at that moment. The plan repeats every week, so it shows on whichever week you are looking at.',
        steps: [
          {
            title: 'Choose Scheduled Coverage',
            body:
              'Open the user, pick a manager or operator role, then select the Scheduled Coverage card under Coverage Method.',
            image: {
              src: 'assets/images/manual/shift-week.png',
              caption: 'Scheduled Coverage selected, with the Monday to Sunday plan below it',
            },
          },
          {
            title: 'Set up your coverage profiles first',
            body:
              'A coverage profile is a reusable template made of a name, a team and a start and end time — for example "Team B – Evening". Select Manage Coverage Profiles, or + Add Coverage Profile, to create one.',
            bullets: [
              'Profile Name — how it reads in the shift list.',
              'Team — the team whose sites the user will cover. + Create Team opens the team drawer if the team does not exist yet.',
              'Start Time and End Time — the hours the profile covers. They must be different.',
            ],
            callout: {
              kind: 'tip',
              text:
                'Build the profiles you use often once. Every shift you add afterwards is then two clicks: pick the profile, pick the days.',
            },
            link: { topicId: 'coverage-profiles' },
          },
          {
            title: 'Add a shift',
            body:
              'Select the + on a day, or + Add beside the week arrows at the top. A panel drops in under the day or the button you clicked, so you can see what you are editing.',
            bullets: [
              'Coverage Profile — pick one or more. Each profile keeps its own hours.',
              'Days — pick every day this shift should run, so one save covers a whole run of days.',
              'Start Time / End Time — only shown when a single profile is picked, to override that profile hours for these days.',
            ],
            video: {
              src: 'assets/videos/manual/assign-shift.mp4',
              poster: 'assets/images/manual/assign-shift-poster.jpg',
              caption: 'Assigning a shift end to end, including creating the coverage profile it uses',
            },
          },
          {
            title: 'Save the shift, then the user',
            body:
              'Select Save Shift — it reads "Save 3 Shifts" when several days are picked. The week grid then shows one line per shift, with hours on the left and the team on the right. Save the user to apply the plan.',
            callout: {
              kind: 'warning',
              text:
                'Saving the user assigns them to every team the plan names. Removing a shift does not remove that team membership on its own.',
            },
          },
          {
            title: 'Edit or remove later',
            body:
              'Each shift line has a pencil and a bin. The pencil re-opens the panel on that shift; the bin drops it from the plan. Use the « and » arrows to move between weeks.',
            image: {
              src: 'assets/images/manual/shift-edit.png',
              caption: 'Edit Shift, opened from the pencil on a shift line',
            },
          },
        ],
        faqs: [
          {
            q: 'A day shows "Off" — is that a problem?',
            a: 'No. Off simply means no shift is planned for that day, so no alerts route to this user then.',
          },
          {
            q: 'I changed a coverage profile. What happens to shifts using it?',
            a: 'Shifts still on the profile old hours move to the new ones. A shift whose hours you changed by hand is left alone.',
          },
          {
            q: 'Can one day cover two teams?',
            a: 'Yes. Add a second shift to the same day and the lines stack, one per team.',
          },
        ],
        children: [
          {
            id: 'coverage-profiles',
            title: 'Coverage profiles',
            tagline: 'Reusable shift templates',
            icon: 'calendar',
            intro:
              'A coverage profile is a saved combination of a team and a pair of hours, under a name you choose. Shifts are built from profiles, so the hours for a team are defined once and reused.',
            steps: [
              {
                title: 'Open the profiles',
                body:
                  'From a shift panel, select Manage Coverage Profiles to see them all, or + Add Coverage Profile to go straight to a new one.',
              },
              {
                title: 'Create one',
                body: 'A profile needs three things.',
                bullets: [
                  'Profile Name — for example "Team B – Evening". This is what the shift list shows.',
                  'Team — whose sites this covers. + Create Team opens the team drawer if it does not exist yet.',
                  'Start Time and End Time — the hours. They must be different.',
                ],
                image: {
                  src: 'assets/images/manual/coverage-profile.png',
                  caption: 'New Coverage Profile, opened from inside the Add Shift panel',
                },
              },
              {
                title: 'Save',
                body: 'Save Profile adds it; Update Profile saves an edit.',
                callout: {
                  kind: 'warning',
                  text:
                    'Editing a profile updates it everywhere it is used. Shifts still on its old hours move to the new ones; a shift whose hours were changed by hand is left alone.',
                },
              },
            ],
            faqs: [
              {
                q: 'How many profiles should we have?',
                a: 'One per team per regular shift pattern. Build those once and adding a week of cover becomes picking a profile and picking the days.',
              },
            ],
          },
        ],
      },
      {
        id: 'manual-assignment',
        title: 'Manual assignment',
        tagline: 'Pin an operator to a team',
        icon: 'assign',
        intro:
          'Manual Assignment pins an operator to one team in real time instead of running a weekly rota. Use it for temporary or manager-controlled cover — someone filling in for a week, or an operator moved onto a team at short notice.',
        steps: [
          {
            title: 'Choose Manual Assignment',
            body:
              'Open the operator and select the Manual Assignment card under Coverage Method. The weekly grid is replaced by Manual Assignment Setup.',
            callout: {
              kind: 'note',
              text:
                'The card is only offered for the operator role. Managers can only use Scheduled Coverage.',
            },
          },
          {
            title: 'Pick the coverage profile',
            body:
              'Select the profile that names the team and hours this operator should cover. This is required — the panel will not save without it.',
            image: {
              src: 'assets/images/manual/manual-assignment.png',
              caption: 'Manual Assignment Setup, with its profile, window and notes',
            },
          },
          {
            title: 'Set the window (optional)',
            body:
              'Start Date and End Date bound how long the cover lasts. Both are optional: leave them empty for cover with no end date. The end must be after the start.',
            callout: {
              kind: 'warning',
              text:
                'These are New York dates and times. The window only narrows the cover — the profile hours still decide which alerts reach the operator inside it.',
            },
          },
          {
            title: 'Record why',
            body:
              'Use Reason / Notes to say why the cover exists, up to 250 characters. It is the only record of the decision once the assignment ends.',
          },
          {
            title: 'Save',
            body:
              'The line under the panel confirms what will happen — which team sites reach the user, during which hours, and that it stops when the assignment ends. Save the user to apply it.',
            callout: {
              kind: 'tip',
              text:
                'The operator returns to scheduled coverage automatically when the assignment ends. There is nothing to switch back by hand.',
            },
          },
        ],
        faqs: [
          {
            q: 'How do I end a manual assignment early?',
            a: 'Open the operator, switch Coverage Method back to Scheduled Coverage and save, or set an End Date in the past.',
          },
          {
            q: 'Does the operator keep their weekly plan?',
            a: 'The plan is kept, but while Manual Assignment is the chosen method it is the assignment that routes their alerts.',
          },
        ],
      },
      {
        id: 'backup-operator',
        title: 'Backup operator',
        tagline: 'Send alerts to a stand-in as well',
        icon: 'backup',
        intro:
          'A backup operator is a stand-in who receives this operator alerts as well. It only adds a recipient — the operator keeps receiving their own alerts, and the backup gains no permissions, groups or sites.',
        steps: [
          {
            title: 'Turn the section on',
            body:
              'Open an operator and scroll to Backup Operator. Use the switch on the right of the section header to enable it.',
            callout: {
              kind: 'note',
              text: 'The section is only shown for the operator role.',
            },
            image: {
              src: 'assets/images/manual/backup-operator.png',
              caption: 'Backup Operator switched on, with one backup card configured',
            },
          },
          {
            title: 'Add the backup',
            body:
              'Select Add Backup Operator, then choose the stand-in from the Backup Operator list. Only other operators can be picked, and one operator can have one backup.',
          },
          {
            title: 'Choose when it applies',
            body: 'Tick one of the two timing options — they are mutually exclusive.',
            bullets: [
              'Same as operator schedule — the backup follows an existing shift. Pick which one in the Schedule field.',
              'Manual schedule set — the backup runs for a stretch you set with Start Date and End Date, in New York time.',
            ],
          },
          {
            title: 'Read the status pill',
            body:
              'The pill at the top of the card shows the state at a glance: blue while it inherits the operator schedule, green inside a live manual period, amber the moment the card own switch is off.',
          },
          {
            title: 'Save and check the roster',
            body:
              'Save the user. The backup then appears as a coverage pill against the operator on the team roster, and counts towards Backup Assigned and Backup Active Now on the Users page.',
            callout: {
              kind: 'tip',
              text:
                'Use the card switch rather than the bin to pause a backup — it keeps the setup in place for next time.',
            },
          },
        ],
        faqs: [
          {
            q: 'Does the operator stop receiving alerts?',
            a: 'No. A backup is additive. Both people receive the alert.',
          },
          {
            q: 'Can I add two backups?',
            a: 'No. The Add Backup Operator button disappears once a card exists.',
          },
          {
            q: 'Does the backup get access to the operator sites?',
            a: 'No. No permissions, groups or sites are granted.',
          },
        ],
      },
    ],
  },
  {
    id: 'organising',
    section: 'users',
    title: 'Organising users',
    topics: [
      {
        id: 'user-grouping',
        title: 'User grouping',
        tagline: 'Sort users into named groups',
        icon: 'group',
        intro:
          'A user group is a named set of users. Groups drive the All Groups filter on the Users page and the User Group field on each account, so they are the quickest way to keep a large estate of users readable.',
        steps: [
          {
            title: 'Open the panel',
            body:
              'Select User Grouping at the top of the Users page. The drawer opens on two tabs — Create Group and Manage Groups, with the current group count next to it.',
            image: {
              src: 'assets/images/manual/user-grouping.png',
              caption: 'The User Grouping drawer on the Create Group tab',
            },
          },
          {
            title: 'Create a group',
            body:
              'On Create Group, give the group a name and select its users. Both are required. Select Create Group to save.',
            callout: {
              kind: 'warning',
              text:
                'A user can be in one group only — adding someone here moves them out of their current group.',
            },
          },
          {
            title: 'Edit or delete',
            body:
              'Switch to Manage Groups to see every group with its member count. Use the search box to find one, then the pencil to rename it or change who is in it, or the bin to delete it. All groups takes you back to the list.',
            image: {
              src: 'assets/images/manual/user-grouping-manage.png',
              caption: 'Manage Groups, listing each group with its member count',
            },
          },
          {
            title: 'Assign from the user panel',
            body:
              'A single user can also be moved without opening this drawer: open them from the Users list and pick a group in the User Group field.',
          },
          {
            title: 'Filter by group',
            body:
              'Back on the Users page, use All Groups above the list to show only the members of one group.',
            callout: {
              kind: 'tip',
              text:
                'Group by what you actually filter by — shift, site cluster or client — rather than mirroring the team hierarchy, which coverage profiles already handle.',
            },
          },
        ],
        faqs: [
          {
            q: 'What is the difference between a group and a team?',
            a: 'A group is a label for organising and filtering users. A team decides alert routing, and is set through coverage profiles and shifts.',
          },
          {
            q: 'What happens to the users when I delete a group?',
            a: 'The accounts are untouched. They simply stop belonging to a group.',
          },
        ],
      },
    ],
  },
  {
    id: 'sites',
    section: 'sites',
    title: 'Sites',
    topics: [
      {
        id: 'sites-page',
        title: 'The Sites page',
        tagline: 'The list and its row actions',
        icon: 'site',
        intro:
          'Sites lists every site on the account. Each row carries its own actions — cameras, alerts, settings, edit and delete — so most work is done from the list rather than a separate detail screen.',
        steps: [
          {
            title: 'Open the page',
            body: 'Select Sites in the left sidebar.',
          },
          {
            title: 'Read the status column',
            body:
              'Status is based on alert activity: a site is Active if it received at least one alert in the last 24 hours, otherwise Inactive. It is not a connection test.',
          },
          {
            title: 'Read the counts and filters',
            body:
              'The cards above the list count the sites, including Recording Started and Cameras Recording. Under them, the toolbar narrows the list.',
            bullets: [
              'Source — only sites that raised alerts from the picked sources.',
              'All sites / Armed / Disarmed — filters by your own quick disarms, across every page.',
              'Recording — sites with RTSP Recording switched on.',
              'Camera Recording — sites with at least one camera actually recording. Its count is cameras, not sites.',
            ],
          },
          {
            title: 'Read the Camera column',
            body: 'Beside the camera icon, small chips sum up the site’s cameras.',
            bullets: [
              '3/5 rec — three of the site’s five cameras are recording. A site with RTSP Recording off reads 0.',
              'Site disarmed — you have quick-disarmed every camera at the site.',
              '2/5 cam disarmed — you have quick-disarmed some of its cameras. Hover the chip for the time left.',
            ],
            callout: {
              kind: 'note',
              text:
                'The site type, when one is set, is shown after the created date under the site name.',
            },
          },
          {
            title: 'Use the row actions',
            body: 'Each row has icons for the jobs covered by this manual.',
            bullets: [
              'Camera icon — opens the Cameras drawer for that site.',
              'Alerts icon — opens every alert recorded for the site, newest first.',
              'Cog icon — opens Site Settings.',
              'Pencil — opens Update Site, the full site form.',
            ],
          },
          {
            title: 'Add a site',
            body:
              'Select New Site. The form opens as a wide drawer; Site Name, Site Display Name, CRM URL and Alert Schedule are all required before Submit will save.',
            callout: {
              kind: 'note',
              text:
                'Operators can view sites, cameras and alerts, but cannot add, edit or delete a site, and cannot save Site Settings.',
            },
            link: { topicId: 'new-site' },
          },
          {
            title: 'Import CRM data',
            body:
              'CRM Data Upload, next to New Site, matches the deals in a CRM Excel export to your sites and assigns them to the dynamic form for their site type.',
            callout: {
              kind: 'note',
              text: 'The button is not shown to operators.',
            },
            link: { topicId: 'crm-data-upload' },
          },
        ],
        faqs: [
          {
            q: 'A site shows Inactive — is it offline?',
            a: 'Not necessarily. Inactive only means no alert arrived in the last 24 hours. Site Monitor is what reports whether a site is up or down.',
          },
          {
            q: 'Why does another user see a different Disarmed list?',
            a: 'Quick disarm is per user. The Armed / Disarmed filter and the disarm chips only read your own disarms.',
          },
        ],
        children: [
          {
            id: 'new-site',
            title: 'Adding a site',
            tagline: 'The New Site form',
            icon: 'site',
            intro:
              'New Site opens a wide form. Four things are required — Site Name, Site Display Name, CRM URL and at least one Alert Schedule — and the rest can be filled in later through Update Site.',
            steps: [
              {
                title: 'Fill the required fields',
                body:
                  'Site Name, Site Display Name and CRM URL each block the save until filled. The display name is what operators read on the dashboard.',
              },
              {
                title: 'Add contacts and speakers',
                body:
                  'Speaker takes one call number per row; Add Speaker adds another and the × removes one. 911 Number to Call and Client Contact Number are the numbers used when an incident is escalated.',
              },
              {
                title: 'Choose the Site Type',
                body:
                  'Construction Site, Residential Site or Commercial Building. Optional — leave it empty and the site stays unclassified. Every alert from a Construction Site shows “Monitoring service started, no workers on site”, so operators know a person on camera is not part of the crew.',
              },
              {
                title: 'Set the Site Options',
                body:
                  'These switches are the site-wide defaults. Per-camera overrides live in the Cameras drawer. A new site starts with Quick disarm, Face detection and Vehicle detection already on — switch off what this site does not need.',
                bullets: [
                  'Quick disarm — only works together with a schedule.',
                  'Face detection and Vehicle detection — what the site looks for.',
                  'Zone Active — lets drawn zones apply.',
                  'RTSP Recording — the master recording switch.',
                  'Send Email Active — reveals Add Email, the address alerts are sent to.',
                ],
              },
              {
                title: 'Assign the team',
                body:
                  'Assign Team decides who the site’s alerts reach, through that team’s coverage schedules. A site with no team has no one to route to.',
              },
              {
                title: 'Add the Alert Schedule',
                body:
                  'Alert Schedule is required on a new site. Select it and add at least one window, or the form will not save.',
                callout: {
                  kind: 'tip',
                  text:
                    'On a new site the schedule screen also offers two shortcuts — 24 hours 7 days a week, and 24 hours for weekends and holiday — and a Grid view for painting the hours.',
                },
                link: { topicId: 'alert-schedule' },
              },
              {
                title: 'Choose the layout',
                body:
                  'New Layout asks for a Server and a Layout Name, and opens Device Create And In Layout, where each device row takes Device Name, Device Type, IP Address / RTSP URL, Username, Password, Description and Source. Existing Layout just picks a saved layout instead.',
                callout: {
                  kind: 'note',
                  text:
                    'Layout can only be chosen while adding. Editing a site offers the existing-layout picker only.',
                },
              },
              {
                title: 'Submit',
                body: 'Select Submit. Discard closes without creating the site.',
              },
            ],
            faqs: [
              {
                q: 'Where are Live URLs on the add form?',
                a: 'They are not. Create the site first, then add them through Update Site.',
              },
            ],
          },
          {
            id: 'live-urls',
            title: 'Live URLs',
            tagline: 'Make a site playable',
            icon: 'camera',
            intro:
              'Live URLs are the RTSP streams for a site. Anything listed here is playable from the dashboard’s live camera view. They are edited through the pencil icon — Update Site — not Site Settings.',
            steps: [
              {
                title: 'Open Update Site',
                body:
                  'Select the pencil on the site row and scroll to Live URLs. An empty site reads "No live urls yet. Add one to make this site playable in live view."',
              },
              {
                title: 'Add a stream',
                body: 'Select Add URL and complete the row.',
                bullets: [
                  'RTSP URL — for example rtsp://host:554/stream.',
                  'Camera Name — how the stream is labelled in live view.',
                  'Username and Password — the stream’s own credentials. The eye icon reveals the password while you check it.',
                ],
                  image: {
                    src: 'assets/images/manual/live-urls.png',
                    caption: 'Live URLs on Update Site (stream addresses blurred here)',
                  },
              },
              {
                title: 'Save',
                body: 'Select Save. Remove drops a row you no longer need.',
                callout: {
                  kind: 'tip',
                  text:
                    'Name the stream the way operators talk about the position — "Yard gate" rather than "cam-04" — because that name is what they see under pressure.',
                },
              },
            ],
            faqs: [
              {
                q: 'A camera raises alerts but will not play live. Why?',
                a: 'Alerts and live view are separate. A camera appears in the Cameras drawer once it alerts, but it only plays if its stream is listed here under Live URLs.',
              },
              {
                q: 'Why does a camera card say No Live URL?',
                a: 'The camera is not linked to any of the streams listed here. It can still alert, but it cannot be recorded, so its Recording pill stays greyed out.',
              },
            ],
          },
          {
            id: 'crm-data-upload',
            title: 'CRM Data Upload',
            tagline: 'Match an Excel export to sites',
            icon: 'clipboard',
            intro:
              'CRM Data Upload reads an Excel export from the CRM, matches each deal to a SightWatch site, and on Save assigns the matched sites to the dynamic form for their site type — filling that form from the Excel row and stamping the site type on each site.',
            steps: [
              {
                title: 'Open it',
                body:
                  'On the Sites page, select CRM Data Upload next to New Site. The Match Excel Sites drawer opens.',
              },
              {
                title: 'Pick the site type, then the file',
                body:
                  'Choose the site type the whole file belongs to — Construction Site, Residential Site or Commercial Building. Upload Excel stays disabled until one is picked. Then upload the .xlsx or .xls file.',
                callout: {
                  kind: 'note',
                  text:
                    'The Deal Name column is what gets matched against site names. A file with no Deal Name rows is refused. The site type is locked once a file is loaded — Clear the file to change it.',
                },
              },
              {
                title: 'Review the matches',
                body: 'Every deal is listed with the site it matched and a badge saying how.',
                bullets: [
                  'Exact — the deal name matched a site name outright.',
                  'Suggested — a close match. Select Confirm to accept it, and it counts as By hand; until then it is not saved.',
                  'By hand — you picked the site(s) yourself. Reset puts back what was suggested.',
                  'Not matched — no site was found. Pick one, or select Add Site to create it without leaving the drawer.',
                ],
                callout: {
                  kind: 'tip',
                  text:
                    'Use the tabs — All, Matched, To review, Not matched and Already in other form — and the search box to work through a long file one kind of row at a time.',
                },
              },
              {
                title: 'Pick sites by hand',
                body:
                  'A deal can be matched to more than one site. Picking a site asks you to confirm the match. Under the picker the row says which dynamic form the site is in now, and warns if the same site is also linked to another row.',
              },
              {
                title: 'Save',
                body:
                  'The bar at the bottom says how many matched sites will be assigned and to which form. Select Save. Only Exact and By hand rows are saved.',
                callout: {
                  kind: 'warning',
                  text:
                    'If a site already belongs to another dynamic form you are asked, per site, to Override (move it to this form) or Skip (leave it where it is). Override all and Skip all set every site at once; Continue saves straight away.',
                },
              },
            ],
            faqs: [
              {
                q: 'A site already had form answers. Are they duplicated?',
                a: 'No. The saved answers for that site are updated from the Excel row, not added a second time.',
              },
              {
                q: 'Why was nothing assigned?',
                a: 'Every site that clashed with another form was skipped, or no row was Exact or By hand. Confirm the Suggested rows first.',
              },
            ],
          },
        ],
      },
      {
        id: 'site-settings',
        title: 'Site settings',
        tagline: 'Schedules, quick disarm, email',
        icon: 'settings',
        intro:
          'The cog on a site row opens Site Settings — the day-to-day switches for one site. The full site record (name, CRM URL, speakers, teams, layout, live URLs) is edited separately through the pencil icon.',
        steps: [
          {
            title: 'Open Site Settings',
            body: 'Select the cog icon on the site row. Changes are held until you select Save.',
            image: {
              src: 'assets/images/manual/site-settings.png',
              caption: 'Site Settings, opened from the cog on a site row',
            },
          },
          {
            title: 'Alert Schedule',
            body:
              'Opens the list of schedules for the site. This is what arms and disarms alerting, and most other settings depend on it.',
            callout: {
              kind: 'note',
              text: 'Hidden for operators — they cannot open or change a schedule.',
            },
            link: { topicId: 'alert-schedule' },
          },
          {
            title: 'Authorised Visitor Alert Schedule',
            body:
              'Attaches a note to every alert raised in a time window — a contractor visit, a delivery slot, an alarm test. Each note has its own window, and you choose whether it applies to all cameras or specific ones.',
            bullets: [
              'Applies to — all cameras of the site, or a chosen camera.',
              'Start Date and End Date — the window the note covers.',
              'Note — shown on the detail page of every alert raised in that window.',
            ],
            callout: {
              kind: 'tip',
              text:
                'This does not silence alerts. It labels them, so whoever reviews the alert knows the activity was expected.',
            },
            link: { topicId: 'visitor-schedule' },
          },
          {
            title: 'Quick disarm',
            body:
              'Works only together with a schedule. Create and trigger a schedule first; alerts then follow that schedule while Quick Arm is enabled. With Quick Arm off, schedule-based alerts are not displayed.',
            callout: {
              kind: 'warning',
              text: 'Turning this on without a schedule in place does nothing.',
            },
          },
          {
            title: 'Send Email Active',
            body:
              'Enable it, then enter an address in Add Email and save, to have alerts for this site emailed. If it is not enabled or not saved, alerts are not sent.',
            callout: {
              kind: 'warning',
              text:
                'Saving with the toggle on but no address is blocked with "Please enter an email address for Send Email Active."',
            },
          },
          {
            title: 'Save',
            body:
              'Select Save. A "Site settings saved successfully" message confirms it. Discard closes without keeping the changes.',
          },
        ],
        faqs: [
          {
            q: 'Where do I change the site name, teams or live URLs?',
            a: 'Those are on the site form behind the pencil icon, not in Site Settings. The form also holds speakers, the 911 and client contact numbers, layout and devices.',
          },
          {
            q: 'Why is the footer showing Close instead of Save?',
            a: 'You are signed in as an operator. Operators can read Site Settings but not save them.',
          },
        ],
        children: [
          {
            id: 'alert-schedule',
            title: 'Alert Schedule',
            tagline: 'The weekly arm plan',
            icon: 'calendar',
            intro:
              'A site’s Alert Schedule is a weekly plan of windows in which alerts are raised. The plan repeats every week. A day with no schedule stays armed around the clock — an empty day is not a quiet day.',
            steps: [
              {
                title: 'Open the plan',
                body:
                  'From Site Settings, select Alert Schedule. The drawer lists Monday to Sunday, with each day’s windows under it and a running count at the bottom.',
                callout: {
                  kind: 'note',
                  text: 'Operators cannot open this screen, and cannot add, edit or delete a schedule.',
                },
              },
              {
                title: 'Read a day',
                body:
                  'A day with nothing on it reads "No schedule — armed all day". Otherwise each line shows the time range and the schedule name, with "Untitled schedule" if it was saved without one.',
                bullets: [
                  '+1d — the window runs past midnight into the next day, and still belongs to the day it started on.',
                  'Every day — the schedule was saved without specific days.',
                ],
                  image: {
                    src: 'assets/images/manual/alert-schedule.png',
                    caption: 'Monday to Thursday scheduled; the rest stay armed all day',
                  },
              },
              {
                title: 'Add a schedule',
                body:
                  'Use Add New Schedule, or the + on a day to start on that day. The panel opens as Add new schedule.',
                bullets: [
                  'Schedule Name — either Already exists, picking a saved schedule from the dropdown, or Custom, typing a new name.',
                  'Days — every day this window should run on.',
                  'Start Time and End Time — the armed window. They cannot be the same.',
                ],
                  image: {
                    src: 'assets/images/manual/alert-schedule-add.png',
                    caption: 'Add new schedule, with Already exists picked',
                  },
                  video: {
                    src: 'assets/videos/manual/add-alert-schedule.mp4',
                    poster: 'assets/images/manual/add-alert-schedule-poster.jpg',
                    caption: 'Adding a custom schedule across all seven days, then saving it',
                  },
                callout: {
                  kind: 'tip',
                  text:
                    'Picking a saved schedule fills the days in for you. The panel says which days it took, so you can remove any this site does not need.',
                },
              },
              {
                title: 'Use the shortcuts and the grid (new sites)',
                body:
                  'While adding a site, the schedule screen has two more tools on its toolbar. Neither is offered when the schedule is opened for an existing site.',
                bullets: [
                  '24 hours 7 days a week — adds a full-day window to every day. Untick it to remove it again.',
                  '24 hours for weekends and holiday — the same, for Saturday and Sunday only.',
                  'Grid view — shows the week as days by hours under the list. Click a 15-minute cell, or drag across cells, to paint the hours alerts are raised; a day name, an hour, AM / PM or the Days corner paints a whole row, column or the grid. Select Save Schedule to turn the painting into schedules.',
                ],
                callout: {
                  kind: 'note',
                  text:
                    'Switching Grid view on or off changes nothing saved. If a shortcut would clash with a window already on those days, the Add panel opens filled in, so you can fix the clash before saving.',
                },
              },
              {
                title: 'Clear any clash before saving',
                body:
                  'The panel checks as you type and blocks Save if the window collides with one already on those days — either an exact duplicate or a partial overlap. Change the days or the hours, and the message clears.',
              },
              {
                title: 'Save',
                body:
                  'Select Save. Editing one day of a schedule that covers several says so plainly: the other days keep the schedule they have now unless you add them above.',
              },
              {
                title: 'Edit or delete',
                body:
                  'The pencil on a line re-opens it; the bin removes it. Deleting takes effect at once — there is no confirmation step.',
                callout: {
                  kind: 'warning',
                  text:
                    'Because there is no confirmation, check the line you are on before selecting the bin.',
                },
              },
            ],
            faqs: [
              {
                q: 'A window ends earlier than it starts. Is that wrong?',
                a: 'No. It runs past midnight and is marked +1d. It still belongs to the day it started on.',
              },
              {
                q: 'I want a site quiet at night. What do I set?',
                a: 'Add a window covering only the hours you want alerts. Alerts are raised inside the scheduled window; leaving a day empty arms it all day instead.',
              },
            ],
          },
          {
            id: 'visitor-schedule',
            title: 'Authorised Visitor Alert Schedule',
            tagline: 'Label expected activity',
            icon: 'clipboard',
            intro:
              'Notes that attach to every alert raised in a time window — a contractor, a delivery, an alarm test. Each note has its own window and can cover the whole site or just some cameras. Available to every role, operators included.',
            steps: [
              {
                title: 'Open it',
                body:
                  'From Site Settings, select Authorised Visitor Alert Schedule. It is also reachable from the site form while adding a site.',
              },
              {
                title: 'Add a note',
                body: 'Select Add Note, then fill the card.',
                bullets: [
                  'Applies to — Apply to all cameras, or pick specific ones. Choosing all clears any specific picks, and the other way round.',
                  'Start Date and End Date — in New York time. End must be after start.',
                  'Note — what a reviewer needs to know, for example "Contractors on the roof".',
                ],
              },
              {
                title: 'Check the window it will really cover',
                body:
                  'Once both dates are valid, a Covers line appears. It adds 30 minutes either side of the window you set, so activity just before or after the slot is still labelled.',
                callout: {
                  kind: 'note',
                  text:
                    'The note is shown on the detail page of every alert raised in that window.',
                },
                    image: {
                      src: 'assets/images/manual/visitor-schedule.png',
                      caption: 'A note covering four cameras, with its Covers line',
                    },
              },
              {
                title: 'Save',
                body:
                  'Select Save. Remove drops a single note, Clear All removes the site’s whole visitor schedule; both ask first.',
              },
            ],
            faqs: [
              {
                q: 'Does this stop the alerts?',
                a: 'No. Alerts are still raised and still reach whoever covers the site. The note only tells the reviewer the activity was expected.',
              },
            ],
          },
        ],
      },
      {
        id: 'cameras',
        title: 'Cameras',
        tagline: 'Detection, zones and priority',
        icon: 'camera',
        intro:
          'The camera icon on a site row opens the Cameras drawer: site-wide switches at the top, then one card per camera. It is where detection, zones, alert priority and quick disarm are set.',
        steps: [
          {
            title: 'Know where cameras come from',
            body:
              'Cameras are not added by hand. A camera appears here once its first alert arrives, and there is no delete control — so an empty list means nothing has reported yet.',
            callout: {
              kind: 'note',
              text:
                'The RTSP URL, camera name and credentials that make a site playable in live view are added on the site form, under Live URLs.',
            },
          },
          {
            title: 'Set the site-wide switches',
            body:
              'The switches at the top — Alert Active, Face detection, Vehicle detection, Zone Active, RTSP Recording — apply to every camera on the site.',
            callout: {
              kind: 'warning',
              text:
                'A per-camera pill stays greyed out until the matching site-wide switch is on. Turn it on here first.',
            },
                image: {
                  src: 'assets/images/manual/cameras.png',
                  caption: 'The site-wide switches at the top of the Cameras drawer',
                },
          },
          {
            title: 'Set alert priority',
            body:
              'The Alert Priority block has one list per level — High, Medium and Low. Pick the cameras for each level; a camera taken out of every list falls back to Low. Add hours beside a level makes it apply only during those hours. The badge on each camera card reflects this and is read-only.',
            link: { topicId: 'alert-priority' },
          },
          {
            title: 'Turn on recording',
            body:
              'RTSP Recording at the top is the site’s master switch; the Recording pill on a card turns one camera on or off. Hovering the pill says which stream it records, for example "Records Live URL 2".',
            bullets: [
              'Live URL 1, Live URL 2 … on a card — the stream from the site’s Live URLs this camera is linked to.',
              'No Live URL on a card — the camera has no stream, so its Recording pill stays greyed out.',
              'No Live URL beside RTSP Recording — none of the site’s cameras has a stream, so the switch cannot be turned on.',
            ],
            callout: {
              kind: 'tip',
              text: 'Add the streams under Live URLs on Update Site first, then come back and switch recording on.',
            },
            link: { topicId: 'live-urls' },
          },
          {
            title: 'Draw a zone',
            body:
              'Create Zone on a camera card opens the zone editor over the camera’s latest still. Click to drop points around the area, then double-click or press Enter to close the shape — three points is the minimum, and fewer is refused with "Please create a zone with at least 3 points".',
            bullets: [
              'Undo and Redo step back and forward, and also work as Ctrl+Z and Ctrl+Y.',
              'Clear removes every zone on that camera.',
              'Zones are numbered automatically — Zone 1, Zone 2 — and cannot be named.',
            ],
            callout: {
              kind: 'warning',
              text:
                'Drawing is not enough on its own: Zone Active must be on for the site and the Zone pill on for the camera, or detection will not use the zone.',
            },
          },
          {
            title: 'Work on a single camera',
            body: 'Each camera card carries its own controls.',
            bullets: [
              'Pencil — rename the camera. This is only the display name; the original camera name cannot be changed.',
              'Select zone — despite the name, this is the camera’s location label, such as Inside or Outside. It does not pick a drawn zone.',
              'Create Zone — opens the zone editor, where detection areas are drawn.',
              'Pills — Alert Active, Person, Vehicle, Zone, Recording, Quick Disarm.',
            ],
            link: { topicId: 'camera-location', label: 'More on the location label' },
            image: {
              src: 'assets/images/manual/camera-card.png',
              caption: 'Camera cards with their pills, location label and Create Zone',
            },
          },
          {
            title: 'Disarm temporarily',
            body:
              'Quick Disarm on a card asks how long to disarm for, then confirm with Disarm. To disarm the whole site, use the Quick Disarm Interval at the top with Disarm All; Add time lets you save a custom interval in hours and minutes. Anything disarmed shows the time left and a Stop, and a disarm only mutes the cameras for you.',
            link: { topicId: 'quick-disarm-interval' },
          },
        ],
        faqs: [
          {
            q: 'Zone detection is not working.',
            a: 'Save the object detection method first, then create the zone. Detection only runs while Zone Active is enabled.',
          },
          {
            q: 'A camera is missing from the list.',
            a: 'It has not raised an alert yet. Cameras are created by their first alert, not added manually.',
          },
          {
            q: 'Why can I not name a zone?',
            a: 'The editor numbers them Zone 1, Zone 2 and so on. The Select zone box on a camera card is a different thing — it labels the camera’s alerts.',
          },
        ],
        children: [
          {
            id: 'alert-priority',
            title: 'Alert priority',
            tagline: 'High, Medium and Low',
            icon: 'bell',
            intro:
              'Priority belongs to the camera, not to the alert. Every alert a camera raises is stamped with that camera’s priority at the moment it arrives, and the level decides how the alert is coloured in the queue.',
            steps: [
              {
                title: 'Understand what priority does',
                body:
                  'It changes the colour an alert is shown in, so an operator scanning the queue can see at a glance which positions matter most. It does not change the order: the queue stays in time order on every tab, newest first.',
                callout: {
                  kind: 'warning',
                  text:
                    'A High camera does not jump the queue. If something must be seen first, priority alone will not do it.',
                },
              },
              {
                title: 'Know the default',
                body:
                  'A camera with nothing set is Low. Priority is treated as an escalation, so every camera starts at the bottom of the scale until someone deliberately lifts it.',
              },
              {
                title: 'Open the lists',
                body:
                  'In the Cameras drawer, the Alert Priority block has one row per level — High, Medium, Low — each holding the cameras at that level.',
              },
              {
                title: 'Move a camera',
                body:
                  'Add the camera to the level you want. A camera can sit in only one level that has no hours, because that level holds all day. Adding it to a second such level is refused with "… is already at High, which runs all day. Give High or Medium a time window first." A camera taken out of every list falls back to Low.',
                  image: {
                    src: 'assets/images/manual/alert-priority.png',
                    caption: 'One camera on High, another on Medium, with the badge on its card',
                  },
              },
              {
                title: 'Check the card',
                body:
                  'Each camera card shows its level as a badge. The badge is read-only — the lists above are the only place it is set.',
              },
              {
                title: 'Give a level hours',
                body:
                  'Select Add hours beside a level, set the Start time and End time, and Save. The level then applies only between those hours, every day; outside them its cameras go back to the level they have all day. The hours show beside the level — select them to change or remove them.',
                bullets: [
                  'This is how one camera can be High overnight and Medium in the day: put it in both lists and give each level its own hours.',
                  'An end earlier than the start runs past midnight; hovering the hours says so.',
                  'Two levels cannot share the same hours. A clash is shown as soon as you pick the times, and Save stays disabled until it is cleared.',
                ],
                callout: {
                  kind: 'warning',
                  text:
                    'Hours on a level with no cameras in it do nothing — the editor warns "No cameras at this level yet". Pick the cameras in the list first.',
                },
              },
            ],
            faqs: [
              {
                q: 'I raised a camera to High. Why are yesterday’s alerts still the old colour?',
                a: 'The level is stamped onto each alert as it arrives, so the change applies to alerts from now on. Existing alerts keep the priority they were raised with.',
              },
              {
                q: 'Which level should a camera be?',
                a: 'Lift the positions where a detection means something has gone wrong — a perimeter gate, a locked yard. Leave routine positions at Low, or the colouring stops telling the operator anything.',
              },
              {
                q: 'Can I sort or filter the queue by priority?',
                a: 'No. The queue is chronological on every tab; priority only colours the card.',
              },
              {
                q: 'Which clock do the priority hours use?',
                a: 'The same New York time as the rest of the app. The hours repeat every day; they cannot be limited to certain weekdays here.',
              },
            ],
          },
          {
            id: 'camera-location',
            title: 'Camera location',
            tagline: 'The Inside / Outside label',
            icon: 'camera',
            intro:
              'Each camera can carry a location label — Inside, Outside, or whatever your account defines. The label and its colour are shown on every alert the camera raises, so an operator can tell at a glance where the detection happened before opening anything.',
            steps: [
              {
                title: 'Pick a label',
                body:
                  'Choose one from the list. It saves as soon as you pick it — there is no Save button on the card — and a "Camera location updated successfully!" message confirms it.',
              },
              {
                title: 'Know what it changes',
                body:
                  'The label is written onto the camera together with its background and text colours, and the alert cards for that camera are tinted with them. It affects how alerts read, not whether they are raised.',
                callout: {
                  kind: 'tip',
                  text:
                    'Unlike alert priority, which only applies to alerts raised from then on, changing a location label also updates the alerts that camera has already raised.',
                },
              },
              {
                title: 'Manage the list',
                body:
                  'The options are not fixed. In Settings, open Manage Camera Location, then Add New Option to create one with a Label, a Background color and a Text color. Existing options can be edited or removed there too.',
                callout: {
                  kind: 'warning',
                  text:
                    'Pick the two colours together and check them side by side — a dark label on a dark fill is unreadable exactly when an operator is scanning quickly.',
                },
              },
            ],
            faqs: [
              {
                q: 'The dropdown is empty. Why?',
                a: 'No location options have been defined yet. Create them in Settings under Manage Camera Location and they appear here.',
              },
              {
                q: 'Is this the same as the zone I drew?',
                a: 'No. A zone limits where detection counts. A location label only describes the camera and colours its alerts.',
              },
              {
                q: 'How many labels should we have?',
                a: 'Few enough that the colours stay distinguishable. Inside and Outside carry most of the value; a long list of similar tints tells an operator nothing.',
              },
            ],
          },
          {
            id: 'quick-disarm-interval',
            title: 'Quick Disarm Interval',
            tagline: 'Mute cameras for a set time',
            icon: 'calendar',
            intro:
              'Quick disarm stops a camera raising alerts for a fixed stretch of time, then arms it again on its own. Use it for work on site — a contractor on a roof, a delivery bay in use — instead of switching Alert Active off and hoping someone remembers to switch it back. A disarm is yours alone: it mutes the cameras for you, and everyone else keeps receiving that site’s alerts.',
            steps: [
              {
                title: 'Pick the interval',
                body:
                  'In the Cameras drawer, Quick Disarm Interval sets how long a disarm lasts. It applies to every camera at this site.',
              },
              {
                title: 'Disarm one camera',
                body:
                  'On a camera card, select the Quick Disarm pill. A "Disarm for" picker opens inline on that card; choose the length and confirm with Disarm, or back out with Cancel. Once it is disarmed the card shows the time left beside the pill, with Stop to arm that camera again straight away.',
              },
              {
                title: 'Disarm the whole site',
                body:
                  'Choose the interval, then select Disarm All. It reads "Disarming…" while it works and mutes every camera at the site for that stretch. A line then appears under the interval with the time remaining, how many cameras are muted, and Stop to arm them all again. The line shows for single-camera disarms too.',
                callout: {
                  kind: 'warning',
                  text:
                    'Disarm All is deliberately a button, not something that fires as you change the dropdown — it silences the entire site, so it should never happen on a mis-click.',
                },
              },
              {
                title: 'See disarmed sites in the list',
                body:
                  'Disarming is done in the Cameras drawer. The Sites list shows the result: a chip in the Camera column reads Site disarmed when every camera is muted, or, for example, 2/5 cam disarmed when only some are. Hover it for the time left.',
                bullets: [
                  'The dropdown beside Source filters the list by Armed or Disarmed, across every page.',
                  'It reads your own disarms, so two people can see different sites as disarmed.',
                ],
              },
              {
                title: 'Add an interval that is not listed',
                body:
                  'Select Add time to open Hours and Minutes, enter the length and select Add. The new interval is saved to the shared list, so it is there next time; Close puts the picker away without adding one.',
                callout: {
                  kind: 'note',
                  text:
                    'Intervals go up to 6 hours. Minutes take 0 to 59 — anything longer belongs in the Hours box.',
                },
              },
              {
                title: 'Let it expire, or stop it early',
                body:
                  'Nothing has to be switched back — the camera arms itself again when the interval runs out. To end it sooner, use Stop beside the countdown, or turn the pill off.',
              },
            ],
            faqs: [
              {
                q: 'How is this different from turning Alert Active off?',
                a: 'Alert Active stays off until someone turns it back on, and it applies to everyone. Quick disarm is yours alone and ends by itself, so a site cannot be left quiet by accident.',
              },
              {
                q: 'I disarmed a site. Why is another user still getting its alerts?',
                a: 'That is intended. A quick disarm mutes the cameras for the person who set it, not for the whole team — so cover is never lost because one person silenced a site. Each user disarms for themselves.',
              },
              {
                q: 'Quick disarm on the site form is a toggle. Is that the same thing?',
                a: 'No. The Quick disarm option in Site Settings works together with the Alert Schedule, and does nothing without one. This interval is the here-and-now mute for cameras.',
              },
              {
                q: 'Can I disarm for a whole day?',
                a: 'No — 6 hours is the limit. For a longer planned window, use an Alert Schedule, or an Authorised Visitor note if the alerts should still arrive but be labelled as expected.',
              },
            ],
          },
        ],
      },
      {
        id: 'site-alerts',
        title: 'Alerts',
        tagline: 'Review a site’s alert history',
        icon: 'bell',
        intro:
          'The alerts icon on a site row opens every alert recorded for that site, newest first, with filters for narrowing down what you are looking for.',
        steps: [
          {
            title: 'Open the alerts drawer',
            body: 'Select the alerts icon on the site row.',
          },
          {
            title: 'Narrow the list',
            body:
              'Filter by search text, a start and end date and time, camera, and status. Select Apply Filters to run the query and Clear Filters to start again.',
            callout: {
              kind: 'tip',
              text:
                'Filter by camera first when checking one position — it is faster than scrolling a busy site.',
            },
                image: {
                  src: 'assets/images/manual/site-alerts.png',
                  caption: 'The site alert history with its filters',
                },
          },
          {
            title: 'Open an alert',
            body: 'Selecting an alert shows its frames, clips and detection details.',
          },
          {
            title: 'Know what controls the flow',
            body: 'If alerts are missing or unwanted, the cause is usually one of these.',
            bullets: [
              'Alert Schedule — arms and disarms alerting for the site.',
              'Quick disarm — only takes effect together with a schedule.',
              'Alert Active — the site-wide and per-camera switches in the Cameras drawer.',
              'Assign Team on the site form — decides who the alert reaches.',
            ],
          },
        ],
        faqs: [
          {
            q: 'Why does an alert carry a note about a visitor?',
            a: 'An Authorised Visitor Alert Schedule covered that time window. The note is shown on the alert detail page so the reviewer knows the activity was expected.',
          },
          {
            q: 'Who receives a site’s alerts?',
            a: 'The teams assigned to the site, through their coverage schedules. See Schedule coverage and Backup operator under Coverage & scheduling.',
          },
        ],
      },
    ],
  },
  {
    id: 'hierarchy',
    section: 'hierarchy',
    title: 'Hierarchy',
    topics: [
      {
        id: 'hierarchy-page',
        title: 'The Hierarchy page',
        tagline: 'Teams and roles',
        icon: 'group',
        intro:
          'Hierarchy holds two things: the teams that alerts are routed to, and the roles that decide what each kind of user can reach. They sit behind the Team Levels and Role Levels tabs.',
        steps: [
          {
            title: 'Open the page',
            body: 'Select Hierarchy in the left sidebar.',
          },
          {
            title: 'Choose a tab',
            body: 'The two tabs answer different questions.',
            bullets: [
              'Team Levels — who covers which sites, in which shifts. This is what alert routing follows.',
              'Role Levels — what each role is allowed to see in the app.',
            ],
              image: {
                src: 'assets/images/manual/hierarchy-page.png',
                caption: 'The Hierarchy page on Team Levels',
              },
            callout: {
              kind: 'note',
              text:
                'A tab only appears if your role has permission for it, so you may see one rather than both.',
            },
          },
          {
            title: 'Know what belongs elsewhere',
            body:
              'A person’s own hours and their backup are set on the Users page, not here. This page shows the result and lets you arrange teams around it.',
          },
        ],
        faqs: [
          {
            q: 'Which page decides who receives an alert?',
            a: 'Both, together. A site is assigned to a team here, and the people on that team cover it according to the schedules set on the Users page.',
          },
        ],
      },
      {
        id: 'teams',
        title: 'Teams',
        tagline: 'Who covers which sites',
        icon: 'group',
        intro:
          'A team groups managers and operators into shifts and points them at a set of sites. Alerts from those sites reach whoever is on the team’s active shift.',
        steps: [
          {
            title: 'Read the table',
            body:
              'Each row is a team, with its managers, operators, DW users and sites. Show Details switches the columns from counts to named pills, so you can see exactly who is on each shift.',
            bullets: [
              'Team Schedule — opens the hours the shift runs on.',
              'Roster — opens the weekly shift roster for that shift.',
              'A "+n backup" pill means operators on that team have stand-ins inherited from the Users page.',
            ],
              image: {
                src: 'assets/images/manual/teams-table.png',
                caption: 'Show Details on, so each shift lists its managers, operators and sites',
              },
          },
          {
            title: 'Create a team',
            body:
              'Select New Team. Team Name and Description are both required, and the team needs at least one shift before it will save.',
          },
          {
            title: 'Build the shifts',
            body:
              'Under Shift Management, Add Shift creates a shift block. Each one needs at least one manager and at least one operator, and every shift needs its own name — duplicates are refused.',
              image: {
                src: 'assets/images/manual/team-form.png',
                caption: 'A team with one shift, its people and its sites',
              },
            callout: {
              kind: 'tip',
              text:
                'Name shifts by when they run, such as Nights or Weekend, because the name is what the roster and the Team Schedule pills show.',
            },
            link: { topicId: 'default-schedule', label: 'What new teams start with' },
          },
          {
            title: 'Point it at sites',
            body:
              'Select sites is required — a team with no sites has no alerts to receive. DW User List is optional.',
          },
          {
            title: 'Save',
            body: 'Select Save, or Update when editing. Discard closes without keeping the changes.',
          },
          {
            title: 'Edit, copy or delete',
            body:
              'The Action column has Edit Team, Copy Team and Delete Team. Copy is the quickest way to build a team that mirrors an existing one.',
            callout: {
              kind: 'warning',
              text:
                'The delete confirmation is worded "Do you want to delete user" — it is the team that is removed, not a user account. Read the name in the message before confirming.',
            },
          },
        ],
        faqs: [
          {
            q: 'Backup pills show on the roster. Can I change them here?',
            a: 'No. Backups are set on the Users page and this page follows them for the window shown. The pill tells you the state — Covering now, Same as operator, Paused, Ended and so on.',
          },
          {
            q: 'An operator sees no Edit or Delete buttons.',
            a: 'That is expected. Operators can read the teams, the schedules and the roster, but cannot add a team, edit one or change the roster.',
          },
        ],
        children: [
          {
            id: 'default-schedule',
            title: 'Default schedule',
            tagline: 'What new teams start with',
            icon: 'calendar',
            intro:
              'The default schedule is the set of hours a newly created team begins with. It saves rebuilding the same nights-and-weekends pattern for every team, and it is the only schedule screen that affects teams you have not made yet.',
            steps: [
              {
                title: 'Open it',
                body:
                  'On Team Levels, select Add Default Schedule. The panel lists one row per schedule, with the hours and days it runs on; expanding a row shows a line per day.',
                callout: {
                  kind: 'note',
                  text:
                    'An empty list reads "No schedules yet — alerts are raised around the clock, every day." Nothing is muted until a schedule exists.',
                },
              },
              {
                title: 'Add a schedule',
                body:
                  'Select Add New Schedule, name it, choose the days and set the start and end times. The plan repeats every week.',
                bullets: [
                  'A window whose end is earlier than its start runs past midnight and is tagged +1d. It still belongs to the day it started on.',
                  'A schedule saved without days applies to every day and is tagged Every day.',
                  'Days with nothing on them are listed as "no schedule, armed all day".',
                ],
              },
              {
                title: 'Set the primary',
                body:
                  'The star marks one schedule as primary. Every team created after that starts with a copy of it on each of its shifts, on the same days and hours.',
                bullets: [
                  'Only one schedule is primary at a time.',
                  'The copy belongs to the new team — editing it there changes nothing here.',
                  'With no primary set, a new team inherits every schedule on this list.',
                ],
                  image: {
                    src: 'assets/images/manual/default-schedule.png',
                    caption: 'The starred schedule, marked Primary',
                  },
                callout: {
                  kind: 'tip',
                  text:
                    'Star the pattern most of your sites run on. Teams that differ can be adjusted afterwards, which is quicker than building every team from nothing.',
                },
              },
              {
                title: 'Adjust a schedule',
                body:
                  'Each row carries its own actions: rename it, add it to more days, edit a day’s hours, or remove a day from it. A schedule already on all seven days says "Runs every day — no days left to add."',
              },
            ],
            faqs: [
              {
                q: 'I changed the default schedule. Did existing teams change?',
                a: 'No. It only applies to teams created after the change. Existing teams keep the copy they were given, and are edited through their own Team Schedule.',
              },
              {
                q: 'How is this different from a site’s Alert Schedule?',
                a: 'A site’s Alert Schedule decides when that site raises alerts. This decides the hours a newly created team starts with. They are separate, and both can apply.',
              },
              {
                q: 'Nothing is starred. What happens?',
                a: 'A new team inherits every schedule on the list, which is rarely what you want. Star one to keep new teams predictable.',
              },
            ],
          },
          {
            id: 'shift-roster',
            title: 'Shift roster',
            tagline: 'Who covers which day',
            icon: 'calendar',
            intro:
              'The roster is a week-by-week grid of a shift: one row per shift, one column per day, and a name in each cell. It is where you say who actually covers Tuesday, as opposed to who is on the team in general.',
            steps: [
              {
                title: 'Open it',
                body:
                  'Select the Roster pill on a team row. The drawer opens on the current week, with « and » to move between weeks and This Week to jump back.',
              },
              {
                title: 'Read the grid',
                body:
                  'Each row shows the shift, its hours and which days it runs. A cell holds whoever covers that day, and a name below it with an arrow is that person’s backup.',
                  image: {
                    src: 'assets/images/manual/shift-roster.png',
                    caption: 'Two shifts across a week, with a backup shown under a name',
                  },
                callout: {
                  kind: 'note',
                  text:
                    'A shift reading "No schedule set" has no hours yet. Set them on the shift’s own schedule, or the roster has nothing to place people into.',
                },
              },
              {
                title: 'Assign someone',
                body:
                  'Select any cell. Tick Operator or Manager — they are mutually exclusive — then pick the person and Save. Remove clears an existing assignment.',
                  image: {
                    src: 'assets/images/manual/roster-cell-editor.png',
                    caption: 'The cell editor, opened on one day of a shift',
                  },
                callout: {
                  kind: 'warning',
                  text:
                    'Assigning someone also puts them on that team. It is not a one-week-only change to their membership.',
                },
              },
              {
                title: 'Fill several days at once',
                body:
                  'Add Shift opens a panel where you pick the people, the coverage profile and every day it should run, then save them together. + Add Coverage Profile creates a new profile without leaving the panel.',
              },
              {
                title: 'Start a week again',
                body:
                  'Reset Week clears every assignment for the week you are looking at.',
                callout: {
                  kind: 'warning',
                  text: 'Reset Week takes effect immediately and does not ask for confirmation.',
                },
              },
            ],
            faqs: [
              {
                q: 'The week is empty and says no one is on the coverage profiles.',
                a: 'Nobody has a shift for that team this week. Put someone on a coverage profile from the Users page, or check another week with « and ».',
              },
              {
                q: 'Do roster edits replace what was set on the Users page?',
                a: 'No, they work together. A plan set from the Users page shows here, and edits you make here are saved per week.',
              },
            ],
          },
        ],
      },
      {
        id: 'role-levels',
        title: 'Role levels',
        tagline: 'What each role can reach',
        icon: 'settings',
        intro:
          'A role is a named set of permissions. Each permission is a tab in the app, so a role decides which pages its users see in the sidebar.',
        steps: [
          {
            title: 'Read the table',
            body:
              'Each row is a role, with the admins assigned to it and a pill for every permission it grants. A role with none reads "No permissions granted".',
          },
          {
            title: 'Create a role',
            body:
              'Select New Role, give it a Role Name, then tick the pages it should reach — Dashboard, Users, Sites, Hierarchy and the rest.',
          },
          {
            title: 'Set the Hierarchy sub-tabs',
            body:
              'Team Levels and Role Levels sit under Inside Hierarchy. They stay locked until Hierarchy itself is ticked; ticking Hierarchy turns both on, and unticking the last one turns Hierarchy back off.',
            callout: {
              kind: 'tip',
              text:
                'Give a shift manager Hierarchy with Team Levels only. They can then arrange teams and rosters without being able to change what anyone is allowed to see.',
            },
          },
          {
            title: 'Save',
            body: 'Select Save. Discard closes without keeping the changes.',
          },
        ],
        faqs: [
          {
            q: 'The super_admin row has no Edit or Remove buttons.',
            a: 'That is deliberate. The super admin role cannot be edited or deleted from this screen, so an account that can fix a mistake always remains.',
          },
          {
            q: 'I removed a permission but the user can still open the page by URL.',
            a: 'Permissions decide what the sidebar shows. They are not a hard gate on every route, so treat them as navigation rather than security.',
          },
          {
            q: 'Why can a user not see a tab I granted?',
            a: 'They need to sign in again for the change to be picked up, and the permission must exist on the server as well as in the list here.',
          },
        ],
      },
    ],
  },
  {
    id: 'dashboard',
    section: 'dashboard',
    title: 'Dashboard',
    topics: [
      {
        id: 'dashboard-page',
        title: 'The Dashboard',
        tagline: 'Where alerts are handled',
        icon: 'dashboard',
        intro:
          'The Dashboard is where alerts arrive and are dealt with. The queue of alerts sits on the left, and the alert you select opens on the right with its footage and every action you can take on it.',
        steps: [
          {
            title: 'Open the page',
            body:
              'Select Dashboard in the left sidebar. It is also the page you land on after signing in.',
          },
          {
            title: 'Choose a queue',
            body:
              'The tabs along the top split alerts by where they stand. The number beside a tab is how many alerts are in it, refreshed every 30 seconds and after every action.',
            bullets: [
              'LIVE — new alerts that are still waiting for you.',
              'RESOLVED — alerts already handled, by anyone.',
              'UNHANDLED — alerts that waited too long on LIVE.',
              'INCIDENT — incidents still awaiting a report. The tab is highlighted while it has any.',
              'OUT OF ZONE and FALSE ALERTS — alerts marked that way and not yet fixed.',
              'SEARCH — find any past alert by date, site, camera and more.',
            ],
            callout: {
              kind: 'note',
              text:
                'Which tabs you see depends on your role. Operators see LIVE, RESOLVED, UNHANDLED and SEARCH; clients see LIVE, RESOLVED and SEARCH; managers and admins see all of them.',
            },
            link: { topicId: 'alert-queue', label: 'How the queue works' },
          },
          {
            title: 'Open an alert',
            body:
              'Select a card in the queue. Its footage, site and actions open on the right. On a small screen the queue and the alert swap places; the list icon at the top takes you back to the queue.',
            link: { topicId: 'alert-details', label: 'Alert details' },
          },
          {
            title: 'Watch the notifications',
            body:
              'Outcomes recorded by other users appear as notices at the top right — Incident, Out of Zone, False Alert, Not a Threat — each saying how long ago it happened. Select one to open that alert.',
            bullets: [
              'Notices stay until you close them.',
              'Only the newest four are shown; "N more notifications" expands the rest.',
              'The Incidents bell in the header lists unread incidents. Selecting one opens it on the INCIDENT tab.',
            ],
            callout: {
              kind: 'note',
              text: 'The Dashboard plays no sound. Keep it in view while on shift.',
            },
          },
        ],
        faqs: [
          {
            q: 'I cannot see the INCIDENT or FALSE ALERTS tabs.',
            a: 'Those tabs are for managers and admins. Operators work from LIVE and UNHANDLED.',
          },
          {
            q: 'Why do I only see some sites’ alerts?',
            a: 'Apart from admins and IT, each user sees the alerts of the sites their teams cover. Team membership is set on the Hierarchy page.',
          },
        ],
        children: [
          {
            id: 'alert-queue',
            title: 'The alert queue',
            tagline: 'Order, groups and escalation',
            icon: 'bell',
            intro:
              'The queue lists the newest alert first on every tab. Priority colours a card but does not move it up, so read the badge rather than relying on position.',
            steps: [
              {
                title: 'Read a card',
                body:
                  'Each card shows a thumbnail, the site and camera, a High, Medium or Low priority badge, the event names, the time in New York time and the camera’s location.',
                link: { topicId: 'alert-priority', label: 'How alert priority is set' },
              },
              {
                title: 'Understand groups',
                body:
                  'On LIVE, UNHANDLED, OUT OF ZONE and FALSE ALERTS, alerts from the same camera within a short window are gathered under one header showing the site, the camera, "N Alerts" and the time range.',
                bullets: [
                  'Select the group header to take the whole group — an outcome then applies to every alert in it.',
                  'Select a single card inside the group to handle only that alert.',
                  'The chevron expands or collapses the group.',
                ],
              },
              {
                title: 'Know when an alert escalates',
                body:
                  'An alert stays on LIVE only for your response window, then moves to UNHANDLED for the next level to pick up. If the alert you have open ages out, the next one is selected for you.',
                bullets: [
                  'Operator — the alert time set under Settings > Alert Escalation Time (3 minutes by default).',
                  'Backup operator — that time plus the backup minutes.',
                  'Manager — twice the alert time plus the backup minutes.',
                  'Admin and IT — three times the alert time plus the backup minutes.',
                ],
                callout: {
                  kind: 'tip',
                  text:
                    'Operators get the backup window on alerts from sites outside their own teams, since they are covering for someone else there.',
                },
              },
              {
                title: 'Mark incidents read',
                body:
                  'On the INCIDENT tab, unread alerts are highlighted and the All, Unread and Read pills filter them. Opening an alert marks it read.',
              },
              {
                title: 'Page through older alerts',
                body:
                  'The pager under the queue moves between pages and sets how many rows a page holds — 10 to 500, with 100 by default.',
                callout: {
                  kind: 'note',
                  text:
                    'New alerts arrive by themselves only while you are on page 1. Return to page 1 to keep the queue live.',
                },
              },
            ],
          },
          {
            id: 'alert-filters',
            title: 'Filters & search',
            tagline: 'Finding a past alert',
            icon: 'search',
            intro:
              'Every tab except LIVE has a Filters button above the queue; on SEARCH the same filters run across the top of the page. A dot on Filters means some are in use.',
            steps: [
              {
                title: 'Set the filters',
                body: 'Fill in only what you need — every field is optional.',
                bullets: [
                  'Start date & time and End date & time — either one alone is allowed.',
                  'Site, then Camera (several can be picked).',
                  'Status.',
                  'Outcome — Not a Threat, Incident, Out of Zone or False Alert.',
                  'Source — Actuate, Frigate, SMTP, FTP, Audio or Anodet.',
                ],
                callout: {
                  kind: 'warning',
                  text: 'An end time earlier than the start time is not accepted. Check both if the date does not stick.',
                },
              },
              {
                title: 'Search',
                body:
                  'Select Search. "No alerts match these filters" means nothing fits; Clear filters brings back the whole queue.',
              },
              {
                title: 'Clear',
                body:
                  'Clear empties the filters. They are also cleared when you switch tab or leave the page.',
              },
            ],
            faqs: [
              {
                q: 'A site is missing from the Site list.',
                a: 'Managers and operators can only filter by the sites their teams cover.',
              },
            ],
          },
        ],
      },
      {
        id: 'alert-details',
        title: 'Alert details',
        tagline: 'Footage, site and actions',
        icon: 'camera',
        intro:
          'The right side of the Dashboard is the selected alert: its footage at the top, the site and camera under it, and the buttons that decide what happens to it.',
        steps: [
          {
            title: 'Review the footage',
            body:
              'The clip plays muted on a loop. Close Video Pane hides it and Show Video brings it back. The images beside it are the frames that raised the alert.',
            bullets: [
              'Select an image to view it full screen. The wheel zooms and dragging moves around.',
              'The magnifier offers Zoom in, Zoom out, Reset view, Download images and Create video.',
              'The small frame strip along the bottom of the image holds every frame of the alert. Select a frame to show it in the viewer; frames never change by themselves.',
            ],
          },
          {
            title: 'Watch live or play back',
            body:
              'When the site has live cameras, Live, Both and Images tabs appear above the footage. Each new alert starts on Images.',
            bullets: [
              'Live | Playback switches between the live stream and the recording, where the camera is recorded.',
              'Site Wall shows the site’s other cameras; Expand to full wall opens them all, and Back to Alert or Esc returns.',
            ],
          },
          {
            title: 'Read the alert',
            body:
              'The header names the site and camera, the source and the time. Select the camera name to list that camera’s other alerts. Below, Review, Is Accurate, Handled By and Alert Fixed By record what has been done.',
            bullets: [
              'A Notice card shows any active notices for the site, one at a time, with the window each one applies to.',
              'Construction sites show the banner "Monitoring service started, no workers."',
            ],
          },
          {
            title: 'Check the site’s details',
            body:
              'CRM Info opens the site’s dynamic form — contacts, access, numbers. Managers and admins can Update it; operators can read it.',
            link: { topicId: 'form-usage', label: 'Site forms' },
          },
          {
            title: 'Share or trigger the camera',
            body: 'Two smaller actions sit beside CRM Info.',
            bullets: [
              'Copy Link — copies a link that opens this exact alert. The button reads Copied for a moment.',
              'Camera Prompt — fires the event rule set up for the site’s camera. No message confirms it.',
            ],
          },
          {
            title: 'Decide the outcome',
            body:
              'Incident opens the response panel. Not a Threat, Out of Zone and False Alert close the alert straight away.',
            link: { topicId: 'alert-outcomes', label: 'Outcomes' },
          },
        ],
        faqs: [
          {
            q: 'The Live tab is missing.',
            a: 'It only shows for sites with a live (RTSP) camera set up.',
          },
          {
            q: 'CRM Info says the form is not available.',
            a: 'No dynamic form is attached to that site. Attach one with Manage Site on the Dynamic Form page.',
          },
        ],
        children: [
          {
            id: 'alert-outcomes',
            title: 'Outcomes',
            tagline: 'Closing an alert',
            icon: 'assign',
            intro:
              'An outcome records what the alert turned out to be and takes it out of the queue. Outcomes are offered on LIVE, UNHANDLED and SEARCH.',
            steps: [
              {
                title: 'Pick the outcome',
                body: 'Select the button that describes the alert.',
                bullets: [
                  'Not a Threat — a real person or vehicle, but nothing wrong.',
                  'Out of Zone — the detection was outside the area being watched.',
                  'False Alert — nothing was there.',
                  'Incident — something needs a response; opens Incident Response.',
                ],
                callout: {
                  kind: 'warning',
                  text:
                    'Not a Threat, Out of Zone and False Alert apply the moment you select them, with no confirmation. On a group they apply to every alert in it — the panel says "Applies to all N alerts in this camera group."',
                },
                link: { topicId: 'incident-response', label: 'Incident Response' },
              },
              {
                title: 'Move on',
                body:
                  'The other outcome buttons lock once one is chosen. The alert moves to RESOLVED, or to the tab for its outcome, and the next alert opens.',
              },
              {
                title: 'Fix the camera, then the alert',
                body:
                  'On OUT OF ZONE and FALSE ALERTS, managers and admins see Alert Fix. Use it once the zone or camera has been corrected; the alert leaves the tab. On a group it fixes every alert in it.',
                callout: {
                  kind: 'tip',
                  text:
                    'These two tabs are a to-do list for camera settings. A camera that keeps appearing there needs its zones or sensitivity looked at.',
                },
                link: { topicId: 'cameras', label: 'Cameras' },
              },
            ],
          },
          {
            id: 'camera-disarm',
            title: 'Camera disarm',
            tagline: 'Pause a camera’s alerts',
            icon: 'backup',
            intro:
              'Disarming stops a camera raising alerts for a set time — useful while workers or a client are on site in view of it.',
            steps: [
              {
                title: 'Open it',
                body:
                  'Select Camera Disarm on the alert. The popup names the site and camera.',
              },
              {
                title: 'Choose how long',
                body:
                  'Pick a time under Select Time — 15 minutes to 6 hours, 15 minutes by default. Set Hourly Time adds your own length in hours and minutes, which is then kept in the list.',
                callout: {
                  kind: 'note',
                  text: 'A disarm cannot be longer than 6 hours.',
                },
              },
              {
                title: 'Disarm',
                body:
                  'Select Disarm. The alert shows a countdown, and the outcome, Camera Prompt, incident report and Alert Fix buttons are hidden until it ends.',
              },
              {
                title: 'End it early',
                body: 'Select Stop Disarm to re-arm the camera straight away.',
                callout: {
                  kind: 'warning',
                  text: 'Stop Disarm takes effect immediately and does not ask for confirmation.',
                },
              },
            ],
            faqs: [
              {
                q: 'How is this different from a site’s Alert Schedule or Quick Disarm Interval?',
                a: 'Camera Disarm is a one-off pause on one camera, started from an alert. The Alert Schedule mutes a whole site on a weekly plan, and the Quick Disarm Interval is set on the camera itself.',
              },
            ],
          },
        ],
      },
      {
        id: 'incident-response',
        title: 'Incident Response',
        tagline: 'Calls and the report',
        icon: 'bell',
        intro:
          'Selecting Incident marks the alert as an incident and opens Incident Response — one panel with every way to act on it, and a timer showing how long the response has taken.',
        steps: [
          {
            title: 'Respond',
            body: 'Each row is one way to respond. A row only appears when the site has that number set.',
            bullets: [
              'Speaker — talk down through the site speaker. Pick one of the speaker numbers.',
              '911 Call — emergency dispatch.',
              'Client Contact — call the client.',
              'Create Incident Report — log what happened.',
            ],
            callout: {
              kind: 'warning',
              text:
                'Selecting a number dials it straight away through your softphone, with no confirmation. The row shows "Calling…" while it connects.',
            },
          },
          {
            title: 'Keep the site details open',
            body:
              'Calling opens the site’s form beside the panel, so the contacts and access notes are in view. The double-arrow at the bottom left shows or hides it.',
          },
          {
            title: 'Move through a group',
            body:
              'On grouped tabs the panel stays open while you move between alerts in the same group. Selecting a different alert closes it.',
          },
          {
            title: 'Close the response',
            body:
              'Close the panel with × or by clicking outside it. Opening the report also closes it and stops the timer.',
          },
        ],
        faqs: [
          {
            q: 'The Speaker or 911 row is missing.',
            a: 'The site has no number for it. Add it to the site, or to a speaker or 911 field on the site’s dynamic form.',
          },
          {
            q: 'I cannot see Create Incident Report.',
            a: 'Reports are filed by managers and admins. Operators and IT respond and leave the report to them.',
          },
        ],
        children: [
          {
            id: 'incident-report',
            title: 'Incident report',
            tagline: 'Internal and client notification',
            icon: 'form',
            intro:
              'The incident report records what happened on an alert. It is built from the incident form on the Dynamic Form page, so its questions are your own.',
            steps: [
              {
                title: 'Open it',
                body:
                  'Select Create Incident Report in Incident Response, or on an alert in the INCIDENT tab. It reads Update Incident Report once one has been saved.',
              },
              {
                title: 'Choose who it is for',
                body:
                  'When the site is linked to the client portal, two cards appear at the top. Save files only the one selected.',
                bullets: [
                  'Client Notification — files the report in the client portal. Selected by default.',
                  'Internal Notification — saves the internal incident report only.',
                ],
              },
              {
                title: 'Fill in the client notification',
                body:
                  'Pick the Type of incident, when and where it happened, What happened, the Action taken and the Result, then write it up in plain words — the way you would tell the client on the phone.',
                bullets: [
                  'Some answers bring follow-up questions, such as how access was gained or which apartment.',
                  'Action taken allows several answers; "None" clears the rest.',
                ],
              },
              {
                title: 'Choose the photos and videos',
                body:
                  'Footage is grouped per alert, with This alert ticked already. Nearby alerts from the same site — a few minutes either side — are offered too. Select all and Clear work per group.',
                callout: {
                  kind: 'warning',
                  text:
                    'With nothing ticked, the report is held out of the client portal. Tick at least one photo or video for the client to receive it.',
                },
              },
              {
                title: 'Fill in the internal report',
                body:
                  'Answer the incident form’s questions. Fields marked * are required, and Save is refused until they are filled.',
                link: { topicId: 'form-builder', label: 'Changing the questions' },
              },
              {
                title: 'Save',
                body:
                  'Select Save. The alert is recorded as an Incident and, from the INCIDENT tab, leaves that queue. Discard closes without saving and leaves the outcome undecided.',
              },
            ],
            faqs: [
              {
                q: '"Please complete the client notification first."',
                a: 'Client Notification is selected but its required questions are not answered. Finish them, or switch to Internal Notification.',
              },
              {
                q: 'It says the incident was filed in the portal but not saved to the history.',
                a: 'The client has it. Only the internal copy failed — open the report again and save to add it to Incident History.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'dynamic-forms',
    section: 'dynamic-form',
    title: 'Dynamic Form',
    topics: [
      {
        id: 'dynamic-form-page',
        title: 'The Dynamic Form page',
        tagline: 'Your forms in one place',
        icon: 'form',
        intro:
          'A dynamic form is a set of fields you design once and fill in per site — contacts, gate codes, speaker and 911 numbers, anything the operators need beside an alert. One special form, the incident form, is what operators fill in when they report on an alert.',
        steps: [
          {
            title: 'Open the page',
            body:
              'Select Dynamic Form in the left sidebar. Under Your forms, each form is a card showing its name, how many fields it has and the first few field labels.',
            callout: {
              kind: 'note',
              text:
                'The sidebar tab follows the Dynamic Form permission on the role, and the per-user Dynamic Form tick on the Users page.',
            },
          },
          {
            title: 'Recognise the incident form',
            body:
              'The card with the bell icon, the Incident badge and the System managed pill is the incident form. It is always listed first.',
            bullets: [
              'Its only action is Edit form template.',
              'It cannot be deleted and is not attached to sites — it applies to every alert.',
            ],
            link: { topicId: 'form-usage', label: 'How the incident form is used' },
          },
          {
            title: 'Create a form',
            body: 'Select New Form. The builder opens on an empty form.',
            link: { topicId: 'form-builder', label: 'Building a form' },
          },
          {
            title: 'Rename a form',
            body:
              'Select the pencil beside a form’s name, type the new name and press Enter or the tick. Esc or the cross cancels. The name cannot be left empty.',
          },
          {
            title: 'Work on a form',
            body: 'Every other card has three actions in its footer.',
            bullets: [
              'Manage Site — choose which sites use this form.',
              'Edit Form (pencil) — reopen the form in the builder.',
              'Delete Form (bin) — remove the form, after a confirmation.',
            ],
            link: { topicId: 'form-sites', label: 'Attaching sites' },
          },
        ],
        faqs: [
          {
            q: 'A form is listed as "Form 2" rather than a name.',
            a: 'It was saved without a Form name. Use the pencil beside it to give it one.',
          },
          {
            q: 'Can I copy an existing form?',
            a: 'Not from this page. Create a New Form and add the fields again.',
          },
        ],
        children: [
          {
            id: 'form-sites',
            title: 'Attaching sites',
            tagline: 'Which sites use a form',
            icon: 'site',
            intro:
              'A form only appears for the sites attached to it. A site can belong to one form at a time, so attaching it here takes it away from whichever form had it before.',
            steps: [
              {
                title: 'Open Manage Site',
                body:
                  'Select Manage Site on the form’s card. The Manage sites in this form panel opens with the sites already attached ticked.',
              },
              {
                title: 'Pick the sites',
                body:
                  'Use Select Sites to add or remove sites. Picking a site that is already in another form shows a warning naming that form — the pick is kept.',
                callout: {
                  kind: 'warning',
                  text:
                    'Saving moves that site: it is added to this form and removed from the other one. The answers saved against the old form no longer show for that site.',
                },
              },
              {
                title: 'Save',
                body: 'Select Save. Discard closes without changing anything.',
              },
              {
                title: 'Attach many sites from a CRM export',
                body:
                  'For a large batch, use CRM Data Upload on the Sites page instead. It matches the Excel Deal Name column to portal sites, attaches the matched sites to the form for the chosen site type, and fills their answers from the Excel columns.',
                callout: {
                  kind: 'tip',
                  text:
                    'CRM Data Upload finds the form by name: the form’s name must contain Construction, Resident or Commercial to match the site type picked in the upload.',
                },
                link: { topicId: 'crm-data-upload', label: 'CRM data upload' },
              },
            ],
            faqs: [
              {
                q: 'A site shows a different form from the one I expected.',
                a: 'A site is meant to sit in one form only. Open Manage Site on the form you want and save the site there; it is removed from the other form.',
              },
            ],
          },
        ],
      },
      {
        id: 'form-builder',
        title: 'Building a form',
        tagline: 'Sections, fields and save',
        icon: 'form',
        intro:
          'The builder has three panels: sections and field types on the left, a live preview of the form in the middle, and the selected field’s settings on the right.',
        steps: [
          {
            title: 'Name the form',
            body:
              'Type a Form name at the top of the preview. This is the name shown on the Dynamic Form page.',
          },
          {
            title: 'Add sections',
            body:
              'Select Add Section to group fields under a heading, such as Contacts or Access. Rename it in Section title, and select a section in the list to add fields to it.',
            bullets: [
              'Sections are optional — fields can be added without one.',
              'Adding the first section moves any loose fields into Section 1.',
              'A section heading in the preview collapses on click; Collapse all and Expand all fold them together.',
            ],
            callout: {
              kind: 'warning',
              text: 'Removing a section removes every field inside it, without asking.',
            },
          },
          {
            title: 'Add fields',
            body:
              'Under Add Fields, select a field type to add it to the selected section. The new field opens in Field Configuration straight away.',
            bullets: [
              'Text — Text Input, Textarea, Email, Password, Phone, URL, Number.',
              'Date and time — Date, Time, Date & Time.',
              'Choices — Checkbox, Radio, Dropdown.',
              'Other — File Upload, Range, Color Picker, Hidden.',
            ],
            link: { topicId: 'field-settings', label: 'Field configuration' },
          },
          {
            title: 'Arrange the fields',
            body:
              'Drag a field by its handle to reorder it, or to move it to another section. The pencil reopens its settings and the bin removes it.',
            callout: {
              kind: 'warning',
              text: 'Removing a field takes effect immediately and does not ask for confirmation.',
            },
          },
          {
            title: 'Save',
            body:
              'Select Save in the page header — it appears once the form has at least one field. You return to the Dynamic Form page.',
            callout: {
              kind: 'warning',
              text:
                'The back arrow leaves without saving and without a warning. Select Save once and wait; a second click can create the form twice.',
            },
          },
        ],
        faqs: [
          {
            q: 'Where do I choose the sites for a new form?',
            a: 'Not in the builder. Save the form, then use Manage Site on its card.',
          },
          {
            q: 'Can I turn a new form into the incident form?',
            a: 'No. There is a single incident form, managed by the system. Edit it with Edit form template on its card.',
          },
        ],
        children: [
          {
            id: 'field-settings',
            title: 'Field configuration',
            tagline: 'Label, options, layout',
            icon: 'settings',
            intro:
              'Selecting a field opens its settings on the right. Changes show in the preview as you make them; there is no separate apply button.',
            steps: [
              {
                title: 'Basic properties',
                body: 'Every field has the same core settings.',
                bullets: [
                  'Label — the question the reader sees.',
                  'Field Name — the key the answer is stored under.',
                  'Placeholder — the grey hint inside an empty box.',
                  'Disabled and Read only — show the field without letting it be changed.',
                ],
                callout: {
                  kind: 'warning',
                  text:
                    'Change the Field Name only on a new form. Answers already saved are stored under the old name, so renaming it or deleting the field hides them. Changing the Label is safe.',
                },
              },
              {
                title: 'Options',
                body:
                  'Dropdown and Radio fields start with three options. Edit their text, remove one with ×, or add more with + Add Option. Tick Multiple Selection to let a dropdown take several answers.',
              },
              {
                title: 'Custom fields for options',
                body:
                  'On a single-choice dropdown, + Add Custom Field adds an extra box that only appears when a particular option is picked — for example a Reason box under "Other". Each has its own Field Name, Label, Placeholder, Type and Required tick.',
              },
              {
                title: 'Numbers and files',
                body: 'Some field types carry settings of their own.',
                bullets: [
                  'Number and Range — Min, Max and Step.',
                  'File Upload — Accept File Types (for example image/*, .pdf) and Multiple Files.',
                ],
              },
              {
                title: 'Layout and appearance',
                body:
                  'Columns sets how wide the field is on a 12-column grid: Auto, or 2 to 12. Two fields set to 6 sit side by side. Prefix and Suffix add fixed text before or after the box, such as +1 or ft.',
              },
              {
                title: 'Incident data source',
                body:
                  'On the incident form only, a field can be linked to User, Team, Speaker or Operator, so its choices come from that list rather than options you type. One source per field.',
              },
            ],
            faqs: [
              {
                q: 'I edited an option but the preview still shows the old text.',
                a: 'The preview catches up when you add or remove an option, or close the panel. The saved form uses the new text.',
              },
            ],
          },
          {
            id: 'field-conditions',
            title: 'Conditions & validation',
            tagline: 'When a field shows, what it accepts',
            icon: 'assign',
            intro:
              'Conditions decide when a field is shown; validation decides what it will accept. Both live in the field’s settings, below the basic properties.',
            steps: [
              {
                title: 'Show a field only when needed',
                body:
                  'Under Condition, select Add Condition, pick the Field it depends on and the When value — Yes, No, Any value, Has value, Is empty, or one of that field’s own options.',
                bullets: [
                  'All conditions (AND) — every rule must hold.',
                  'Any condition (OR) — one is enough.',
                  'Hide field permanently — never shown, but its value is still saved.',
                ],
                callout: {
                  kind: 'tip',
                  text:
                    'Example: show a Textarea only when a Dropdown is Yes. Fields hidden by a condition are faded in the builder preview so you can still edit them.',
                },
              },
              {
                title: 'Make a field required',
                body: 'Under Validation, tick Required. The form cannot be saved until it is filled.',
              },
              {
                title: 'Limit what it accepts',
                body: 'The limits on offer depend on the field type.',
                bullets: [
                  'Text and Textarea — Min Length and Max Length.',
                  'Text and Phone — Pattern (Regex), for example ^[0-9]+$ for digits only.',
                  'Number and Range — Min Value and Max Value.',
                ],
              },
              {
                title: 'Word the error messages',
                body:
                  'Custom Error Messages replace the default wording, such as "This field is required" or "Invalid format". Leave them empty to keep the defaults. {{min}}, {{max}} and {{requiredLength}} are filled in with the field’s own limits.',
              },
            ],
          },
        ],
      },
      {
        id: 'form-usage',
        title: 'Where forms are used',
        tagline: 'Sites, alerts and incidents',
        icon: 'bell',
        intro:
          'Site forms are read beside an alert, and the incident form records what happened on one. Each site keeps one set of answers per form.',
        steps: [
          {
            title: 'Fill in a site’s details',
            body:
              'On the Sites page, select a site’s name to open its form. Fill it in and Submit; afterwards it opens as a read-only summary, with Update to change it.',
            callout: {
              kind: 'note',
              text:
                'Operators can read a site’s details but cannot change them. "No details saved yet." means the form has not been filled in for that site.',
            },
          },
          {
            title: 'Read it on an alert',
            body:
              'On the Dashboard, CRM Info in the alert details opens the site’s form. In Incident Response, Show dynamic form details docks it beside the popup.',
            bullets: [
              'Fields whose name or label mentions speaker or 911 supply the numbers used by the speaker and police actions.',
            ],
            link: { topicId: 'alert-details', label: 'Alert details' },
          },
          {
            title: 'File an incident report',
            body:
              'Create Incident Report on an alert opens the incident form. Fill it in and Save; the button then reads Update Incident Report.',
            callout: {
              kind: 'note',
              text:
                'When the site is linked to the client portal, choose Client Notification to file it in the portal or Internal Notification to keep it internal.',
            },
          },
          {
            title: 'Review past incidents',
            body:
              'Incident History lists every report with its answers grouped by section. Filter by Incident field and Answer to find, for example, every incident where Police called is Yes.',
          },
        ],
        faqs: [
          {
            q: 'I changed the incident form. Do old reports change?',
            a: 'No. Each report keeps the questions and answers it was saved with. Old reports may not match new filters if a field was renamed or removed.',
          },
          {
            q: 'A site says "Dynamic form not available for this site".',
            a: 'No form is attached to that site. Attach it with Manage Site on the Dynamic Form page.',
          },
        ],
      },
    ],
  },
];
